import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const AdminOverview = () => {
  const [stats, setStats] = useState({});
  const [trends, setTrends] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      const [statsResponse, activityResponse] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/recent-activity')
      ]);
      
      setStats(statsResponse.data.stats);
      setTrends(statsResponse.data.stats.trends || {});
      setRecentActivity(activityResponse.data.activities || []);
    } catch (error) {
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, gradient, trend, trendValue, delay = 0 }) => (
    <div 
      className={`bg-white rounded-3xl shadow-xl p-6 border-2 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-in-up`}
      style={{ animationDelay: `${delay}ms`, borderColor: '#DFD0B8' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium mb-2" style={{ color: '#393E46' }}>{title}</p>
          <p className="text-3xl font-bold mb-2" style={{ color: '#222831' }}>{value}</p>
          {trend && (
            <div className={`flex items-center text-sm font-medium ${
              trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                trend === 'up' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                <i className={`fas fa-arrow-${trend} text-xs`}></i>
              </div>
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div 
          className={`w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg`}
          style={{ backgroundColor: gradient === 'from-blue-500 to-blue-600' ? '#222831' : 
                                   gradient === 'from-green-500 to-green-600' ? '#948979' :
                                   gradient === 'from-purple-500 to-purple-600' ? '#393E46' : '#948979' }}
        >
          <i className={`${icon} text-white text-2xl`}></i>
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ icon, title, description, gradient, onClick }) => (
    <button 
      onClick={onClick}
      className="p-6 rounded-2xl border-2 border-dashed hover:border-transparent hover:shadow-xl transition-all duration-300 text-center group bg-white hover:bg-gray-50"
      style={{ borderColor: '#DFD0B8' }}
    >
      <div className={`w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
           style={{ backgroundColor: gradient === 'from-blue-500 to-blue-600' ? '#222831' : 
                                    gradient === 'from-green-500 to-green-600' ? '#948979' :
                                    gradient === 'from-purple-500 to-purple-600' ? '#393E46' : '#948979' }}>
        <i className={`${icon} text-white text-xl`}></i>
      </div>
      <h3 className="font-semibold mb-2" style={{ color: '#222831' }}>{title}</h3>
      <p className="text-sm" style={{ color: '#393E46' }}>{description}</p>
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading dashboard insights...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Enhanced Header */}
      <div className="text-center mb-8 lg:mb-12">
        <div className="inline-flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-chart-line text-white text-lg lg:text-xl"></i>
          </div>
          <h1 className="text-2xl lg:text-4xl font-bold" style={{ color: '#222831' }}>
            Platform Overview
          </h1>
        </div>
        <p className="text-base lg:text-lg max-w-2xl mx-auto px-4" style={{ color: '#393E46' }}>
          Monitor your VestrioZ platform performance and key metrics with real-time insights
        </p>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <StatCard
          title="Total Users"
          value={stats.totalUsers || 0}
          icon="fas fa-users"
          gradient="from-blue-500 to-blue-600"
          trend={trends.users?.direction || null}
          trendValue={trends.users && trends.users.value !== undefined ? `${trends.users.direction === 'up' ? '+' : '-'}${trends.users.value}% ${trends.users.period}` : null}
          delay={0}
        />
        <StatCard
          title="Active Listings"
          value={stats.activeListings || 0}
          icon="fas fa-list"
          gradient="from-green-500 to-green-600"
          trend={trends.activeListings?.direction || null}
          trendValue={trends.activeListings && trends.activeListings.value !== undefined ? `${trends.activeListings.direction === 'up' ? '+' : '-'}${trends.activeListings.value}% ${trends.activeListings.period}` : null}
          delay={100}
        />
        <StatCard
          title="Total Revenue"
          value={`₹${stats.totalRevenue || 0}`}
          icon="fas fa-rupee-sign"
          gradient="from-purple-500 to-purple-600"
          trend={trends.revenue?.direction || null}
          trendValue={trends.revenue && trends.revenue.value !== undefined ? `${trends.revenue.direction === 'up' ? '+' : '-'}${trends.revenue.value}% ${trends.revenue.period}` : null}
          delay={200}
        />
        <StatCard
          title="Successful Rentals"
          value={stats.successfulRentals || 0}
          icon="fas fa-handshake"
          gradient="from-orange-500 to-orange-600"
          trend={trends.rentals?.direction || null}
          trendValue={trends.rentals && trends.rentals.value !== undefined ? `${trends.rentals.direction === 'up' ? '+' : '-'}${trends.rentals.value}% ${trends.rentals.period}` : null}
          delay={300}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
        <StatCard
          title="Expired Listings"
          value={stats.expiredListings || 0}
          icon="fas fa-clock"
          gradient="from-red-500 to-red-600"
          delay={400}
        />
        <StatCard
          title="Pending Payments"
          value={stats.pendingPayments || 0}
          icon="fas fa-hourglass-half"
          gradient="from-yellow-500 to-yellow-600"
          delay={500}
        />
        <StatCard
          title="New Users Today"
          value={stats.newUsersToday || 0}
          icon="fas fa-user-plus"
          gradient="from-indigo-500 to-indigo-600"
          delay={600}
        />
      </div>

      {/* Enhanced Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Quick Actions */}
        <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6 lg:mb-8">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-bolt text-white text-sm lg:text-base"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Quick Actions</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:gap-4">
            <QuickActionCard
              icon="fas fa-user-plus"
              title="Add User"
              description="Create new user account"
              gradient="from-blue-500 to-blue-600"
            />
            <QuickActionCard
              icon="fas fa-list-alt"
              title="View Listings"
              description="Manage all listings"
              gradient="from-green-500 to-green-600"
            />
            <QuickActionCard
              icon="fas fa-chart-bar"
              title="Analytics"
              description="View detailed reports"
              gradient="from-purple-500 to-purple-600"
            />
            <QuickActionCard
              icon="fas fa-cog"
              title="Settings"
              description="Platform configuration"
              gradient="from-orange-500 to-orange-600"
            />
          </div>
        </div>

        {/* Enhanced Recent Activity */}
        <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
          <div className="flex items-center space-x-3 mb-6 lg:mb-8">
            <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
              <i className="fas fa-history text-white text-sm lg:text-base"></i>
            </div>
            <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Recent Activity</h3>
          </div>
          <div className="space-y-3 lg:space-y-4 max-h-64 lg:max-h-80 overflow-y-auto custom-scrollbar">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 lg:space-x-4 p-3 lg:p-4 rounded-2xl hover:bg-gray-50 transition-colors duration-200">
                  <div 
                    className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md"
                    style={{ backgroundColor: activity.color || '#948979' }}
                  >
                    <i className={`${activity.icon || 'fas fa-info'} text-white text-xs lg:text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm lg:text-base font-semibold mb-1" style={{ color: '#222831' }}>{activity.title}</p>
                    <p className="text-xs lg:text-sm mb-2" style={{ color: '#393E46' }}>{activity.description}</p>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#948979' }}></div>
                      <p className="text-xs font-medium" style={{ color: '#393E46' }}>{activity.time}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 lg:py-12">
                <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#DFD0B8' }}>
                  <i className="fas fa-history text-xl lg:text-2xl" style={{ color: '#948979' }}></i>
                </div>
                <p className="font-medium mb-2 text-sm lg:text-base" style={{ color: '#222831' }}>No recent activity</p>
                <p className="text-xs lg:text-sm" style={{ color: '#393E46' }}>Activity will appear here as users interact with your platform</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Platform Health */}
      <div className="bg-white rounded-3xl shadow-xl p-6 lg:p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6 lg:mb-8">
          <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-heartbeat text-white text-sm lg:text-base"></i>
          </div>
          <h3 className="text-xl lg:text-2xl font-bold" style={{ color: '#222831' }}>Platform Health</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[
            { icon: 'fas fa-server', title: 'Server Status', status: 'Online', color: '#948979' },
            { icon: 'fas fa-database', title: 'Database', status: 'Connected', color: '#222831' },
            { icon: 'fas fa-credit-card', title: 'Payment Gateway', status: 'Active', color: '#393E46' },
            { icon: 'fas fa-envelope', title: 'Email Service', status: 'Operational', color: '#948979' }
          ].map((item, index) => (
            <div key={index} className="text-center p-4 lg:p-6 bg-white rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 border-2" style={{ borderColor: '#DFD0B8' }}>
              <div className={`w-12 h-12 lg:w-16 lg:h-16 rounded-2xl mx-auto mb-3 lg:mb-4 flex items-center justify-center shadow-lg`} style={{ backgroundColor: item.color }}>
                <i className={`${item.icon} text-white text-base lg:text-xl`}></i>
              </div>
              <h4 className="font-semibold mb-2 text-sm lg:text-base" style={{ color: '#222831' }}>{item.title}</h4>
              <div className="flex items-center justify-center space-x-2">
                <div className={`w-2 h-2 rounded-full animate-pulse`} style={{ backgroundColor: item.color }}></div>
                <p className={`text-xs lg:text-sm font-medium`} style={{ color: item.color }}>{item.status}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #a855f7, #3b82f6);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #9333ea, #2563eb);
        }
      `}</style>
      </div>
    </div>
  );
};

export default AdminOverview;